// Timezone-aware timestamp formatting.
//
// The backend stores timestamps as *naive UTC* — datetime.utcnow().isoformat()
// produces e.g. "2026-06-20T07:34:00.123" with NO "Z" or offset. If you hand
// that straight to `new Date()`, the browser interprets it as LOCAL time, so
// the displayed clock shows the UTC wall-clock number (7:34) instead of the
// facility's real local time. These helpers (a) normalize the string to a true
// UTC instant, then (b) render it in the facility's timezone.

// Append "Z" when an ISO string carries no timezone marker, so Date parses it
// as UTC rather than local. Date-only strings are left alone (already UTC).
const asUtcDate = (iso) => {
  if (!iso) return null;
  let s = String(iso);
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  if (!hasZone && (s.includes('T') || /\d{1,2}:\d{2}/.test(s))) {
    s = `${s}Z`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The 4-digit year for a Date *as seen in* a given timezone — used to decide
// whether to append the year (respecting the facility's zone, not the viewer's).
const yearInTz = (d, timeZone) =>
  d.toLocaleDateString('en-US', { ...(timeZone ? { timeZone } : {}), year: 'numeric' });

// Stamp for a conversation/list row: date + time, e.g. "Jun 20, 2:34 AM"
// (year appended when it's a prior year). Rendered in `timeZone`.
export const formatRowStamp = (iso, timeZone) => {
  const d = asUtcDate(iso);
  if (!d) return '';
  const now = new Date();
  const sameYear = yearInTz(d, timeZone) === yearInTz(now, timeZone);
  return d.toLocaleString('en-US', {
    ...(timeZone ? { timeZone } : {}),
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
};

// True when an ISO string has NO time component — a calendar date like
// "2026-06-01" (due_date, start_date). These must NOT be shifted across
// zones (UTC midnight rendered in Central would slip to the prior day), so
// they're always rendered in UTC.
const isDateOnly = (iso) => {
  const s = String(iso || '');
  return !(s.includes('T') || /\d{1,2}:\d{2}/.test(s));
};

// Date-only display, e.g. "Jun 1, 2026". Calendar dates render in UTC (no
// shift); datetime instants render in the facility timezone.
export const formatDate = (iso, timeZone) => {
  const d = asUtcDate(iso);
  if (!d) return '';
  const tz = isDateOnly(iso) ? 'UTC' : (timeZone || undefined);
  return d.toLocaleDateString('en-US', {
    ...(tz ? { timeZone: tz } : {}),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Date + time display for a stored instant, e.g. "Jun 20, 2026, 2:34 AM",
// rendered in the facility timezone. Use for created_at / sent_at / paid_at /
// audit timestamps — anything with a clock component.
export const formatDateTime = (iso, timeZone) => {
  const d = asUtcDate(iso);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    ...(timeZone ? { timeZone } : {}),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Stamp for a message bubble: always date + time, e.g. "Jun 20, 2:34 AM"
// (year appended when it's a prior year). Rendered in `timeZone`.
export const formatThreadStamp = (iso, timeZone) => {
  const d = asUtcDate(iso);
  if (!d) return '';
  const now = new Date();
  const sameYear = yearInTz(d, timeZone) === yearInTz(now, timeZone);
  return d.toLocaleString('en-US', {
    ...(timeZone ? { timeZone } : {}),
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
};
